/** The environment badge (SBX D8 "Console operator pages show an environment badge"): a sandbox is a
 *  tenant of its own (`<production code>sbx`), easy to mistake for its production operator by name.
 *
 *  - a sandbox: an outlined chip with the flask — the same mark the delivery logs give a sandbox's
 *    captured sends — reading "Sandbox";
 *  - a production operator that has a sandbox: "Production", so the pair is told apart at a glance;
 *  - any other operator: nothing (every operator is production unless it says otherwise).
 *
 *  Never colour alone: the chip always carries its word. */
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { Chip, Tooltip } from '@mui/material';

import type { TourOperatorEnvironment } from '@/api/tourOperators';

export function EnvironmentBadge({
  environment,
  paired = false,
  size = 'small',
}: {
  environment: TourOperatorEnvironment | undefined;
  /** A production operator with a sandbox: badge it too. */
  paired?: boolean;
  size?: 'small' | 'medium';
}) {
  if (environment === 'sandbox') {
    return (
      <Tooltip
        describeChild
        title="A sandbox: its own tenant for developing an integration. Its sends stay in its outbox unless allowlisted."
      >
        <Chip
          size={size}
          variant="outlined"
          icon={<ScienceOutlinedIcon />}
          label="Sandbox"
          data-environment="sandbox"
          sx={{ flex: 'none' }}
        />
      </Tooltip>
    );
  }
  if (environment === 'production' && paired) {
    return (
      <Tooltip describeChild title="The production operator. Its sandbox is a separate tenant.">
        <Chip
          size={size}
          variant="outlined"
          icon={<PublicOutlinedIcon />}
          label="Production"
          data-environment="production"
          sx={{ flex: 'none' }}
        />
      </Tooltip>
    );
  }
  return null;
}
